/**
 * DrugSidePanel — the compatibility app's right-hand panel.
 *
 * ONE model. The panel holds the two sides of the Y. You fill a side however
 * you like — type a name in the search box, or tap a drug / cell in the grid —
 * and you can mix freely (search one side, tap the other; search both; tap
 * both). The board merges whatever is searched with whatever is tapped into
 * ≤2 sides and hands them here as `items`.
 *
 *   - Each side → that drug's own grounded medication card (SAI/dm+d), in its
 *     own window. A typed name the matrix doesn't carry is looked up verbatim;
 *     a matrix drug via its SAI-resolvable trade name. An absent drug renders a
 *     plain "not found" — never a fabrication.
 *   - When BOTH sides hold matrix drugs the matrix scores → the Y-site verdict
 *     ("A ↔ B"), from the institution's matrix.
 *
 * A drug is for looking up; a pair is for comparing. There is NO LLM in this
 * panel, so it can neither improvise a drug summary nor invent a verdict.
 *
 * Close affordance: the close X lives in the PARENT (CompatibilityBoard),
 * pinned outside the ScrollArea so it stays visible regardless of scroll.
 */

import { useTranslation } from 'react-i18next'
import { Grid2x2, Pill } from 'lucide-react'
import { useMedicationLookups } from '@frontend/hooks/useMedicationSearch'
import { cn } from '@/lib/utils'
import { MedicationCard } from './MedicationCard'
import type { CompatibilityCell, CompatibilityDrug } from '@frontend/components/ysite/types'
import { VERDICT_TONE } from '@frontend/components/ysite/types'
import { drugSearchTerm } from './drugSearchTerm'
import { shortLabel } from '@frontend/components/ysite/label'
import { TONE_TEXT } from '@frontend/components/ysite/verdict.ui'

/**
 * One side of the panel: either a matrix drug (tapped, or matched from a typed
 * term — an "axis") or a free-text name the matrix doesn't carry. Both resolve
 * to a grounded MedicationCard; only matrix drugs can form a Y-site verdict.
 */
export type PanelItem =
  | { kind: 'drug'; drug: CompatibilityDrug }
  | { kind: 'name'; name: string }

interface DrugSidePanelProps {
  /** The drugs/names in play (≤2), each rendered as its own medication card. */
  items: PanelItem[]
  /**
   * Y-site verdict cell — non-null only when `items` holds exactly two matrix
   * drugs the matrix has an entry for. Drives the "A ↔ B" verdict header.
   */
  cell: CompatibilityCell | null
}

/** Short display name for a panel item (used in the "A ↔ B" verdict header). */
function itemLabel(item: PanelItem): string {
  return item.kind === 'drug' ? shortLabel(item.drug) : item.name
}

/** SAI lookup term for a panel item. */
function itemTerm(item: PanelItem): string {
  return item.kind === 'drug' ? drugSearchTerm(item.drug) : item.name
}

/** Stable React key for a panel item (independent of its SAI term). */
function itemKey(item: PanelItem): string {
  return item.kind === 'drug' ? `d:${item.drug.id}` : `n:${item.name.toLowerCase()}`
}

export function DrugSidePanel({ items, cell }: DrugSidePanelProps) {
  const { t } = useTranslation()

  // Hooks run unconditionally — look up every side's term before any return.
  // `useMedicationLookups` returns one lookup per term, in order, so
  // `lookups[i]` aligns with `items[i]`.
  const lookups = useMedicationLookups(items.map(itemTerm))

  // Empty — nothing searched, nothing tapped.
  if (items.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-7 py-14 text-center">
        <Grid2x2 className="h-12 w-12 text-neutral-300" strokeWidth={1.4} aria-hidden />
        <p className="mt-4 font-display text-[17px] font-semibold tracking-[-0.3px] text-neutral-900">
          {t('compatibility.panel.emptyTitle')}
        </p>
        <p className="mt-1.5 max-w-[260px] text-[14px] leading-relaxed text-neutral-500">
          {t('compatibility.panel.emptyHint')}
        </p>
      </div>
    )
  }

  const tone = cell ? VERDICT_TONE[cell.verdict] : null
  const drugItems = items.filter((i) => i.kind === 'drug')
  const pairLabel =
    drugItems.length === 2
      ? `${itemLabel(drugItems[0])} ↔ ${itemLabel(drugItems[1])}`
      : null

  return (
    <div className="p-6">
      {/* Verdict — only when both sides hold matrix drugs the matrix scores.
          Shown plainly as "A ↔ B" so it can never read as one-sided. */}
      {cell && tone && (
        <div className="mb-5">
          <p className="font-body text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-400">
            {t('compatibility.panel.verdictTitle')}
          </p>
          {pairLabel && (
            <p className="mt-1.5 font-body text-[13px] font-semibold text-neutral-600">
              {pairLabel}
            </p>
          )}
          <div className="mt-2.5 flex items-start gap-2.5">
            <span
              aria-hidden
              className={cn(
                'mt-1.5 h-2.5 w-2.5 flex-shrink-0 rounded-full',
                `bg-tone-${tone === 'muted' ? 'mute' : tone}`,
              )}
            />
            <p
              className={cn(
                'font-display text-[19px] font-semibold leading-tight tracking-[-0.3px]',
                TONE_TEXT[tone],
              )}
            >
              {t(`compatibility.verdicts.${cell.verdict}`)}
            </p>
          </div>
          {cell.derived && (
            <p className="mt-3.5 rounded-lg bg-neutral-100 px-3 py-2.5 text-[12px] leading-relaxed text-neutral-700">
              {t('compatibility.derivedNote')}
            </p>
          )}
        </div>
      )}

      {/* One grounded medication card per side — each in its own window. */}
      <section className={cn(cell && tone && 'border-t border-[var(--divider)] pt-5')}>
        <p className="flex items-center gap-1.5 font-body text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-400">
          <Pill className="h-3 w-3" aria-hidden />
          {t('compatibility.panel.drugInfoTitle')}
        </p>
        <div className="mt-3 flex flex-col gap-2.5">
          {lookups.map((lookup, i) => (
            <MedicationCard key={itemKey(items[i])} lookup={lookup} />
          ))}
        </div>
      </section>
    </div>
  )
}

